import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CartItem } from '../interfaces/cart-item';
import { UserService } from './user.service';
import { CurrencyService } from './currency.service';
import { CultureService } from '../culture/culture.service';

declare var fbq: (...args: any[]) => void;

/// Which product list an impression or a click came from, so GA4 can report per list.
export interface AnalyticsList {
    id: string;
    name: string;
}

/**
 * Google Analytics 4 for the storefront - the one place the browser reports to it.
 *
 * Why it is shaped this way:
 *
 * ONE PROPERTY, SENT DIRECTLY. Measurement used to be split three ways: Firebase Analytics
 * sent to one property, the Tag Manager container to a second, and an older hand-injected tag
 * to a third. Each held part of the picture. gtag.js is now loaded here for
 * environment.analytics.measurementId and nothing else in the app talks to GA. Firebase
 * Analytics is gone with it: it refused to start in browsers without IndexedDB and stalled
 * behind a Firebase Installations call that ad blockers commonly refuse, silently losing
 * those visitors entirely.
 *
 * PAGE VIEWS ARE SENT BY HAND. This is a single-page app, so only the first page is a real
 * page load; every later page is a router navigation. The Tag Manager container fired on page
 * load only, which is why pages reached by clicking around never registered. Pages are sent on
 * NavigationEnd with automatic page views switched off - the GA4 stream's "page changes based
 * on browser history events" setting must be off too, or every page counts twice. The title is
 * given a moment to change first, because Angular sets it after the navigation completes and a
 * page view sent immediately reports the PREVIOUS page's title.
 *
 * PURCHASES ARE NOT SENT FROM HERE. An order is often completed by the payment callback after
 * the buyer has left, so the server reports purchases (GoogleAnalyticsReporter).
 *
 * CONSENT is defaulted in index.html, before Tag Manager or gtag.js can load, and granted here
 * when the cookie notice is accepted.
 *
 * THE OWNER IS NOT MEASURED (the owner, 2026-09-28). With the shop's owner signed in, gtag.js is
 * never loaded and nothing is sent - no page views, no ecommerce, not the data layer mirror or the
 * pixel calls below - so their own browsing, testing and checking do not count as visits, carts or
 * checkouts. Signing in reloads the page, so the check at init() is the one that matters; every send
 * checks again for a sign-in that did not reload. The server leaves the owner's own purchases out
 * the same way (GoogleAnalyticsReporter, byOwner).
 *
 * Everything is a no-op on the server render, and nothing here may throw into a caller - these
 * calls sit in the cart and checkout, where an exception costs a sale.
 */
@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {

    /// Every amount in the app is in the base currency; the shopper's currency is display only.
    static get Currency(): string { return CurrencyService.site.code; }

    /// How long a page view waits for the page to set its own title.
    private static readonly TitleWaitMs = 1000;

    private readonly isBrowser: boolean;
    private started = false;
    private ownerOff = false;
    private userId: string = null;

    private lastLocation: string = null;
    private pending: { location: string, referrer: string } = null;
    private pendingTimer: any = null;

    /// List impressions already sent on the current page, so a list re-rendered with the same
    /// products is not counted again. Cleared on every page view.
    private listsSeen = new Set<string>();

    /// When each one-off user action was last reported, to drop the echo of the same action.
    /// Two DOM quirks deliver a single action twice: Enter in the search box also "clicks" the
    /// form's submit button (implicit submission), and on touch screens the appClick directive
    /// clicks the element itself while the browser's own click still follows - its
    /// preventDefault() runs in a passive listener, where browsers ignore it.
    private lastAction = new Map<string, number>();
    private static readonly EchoWindowMs = 1000;

    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject(DOCUMENT) private document: any,
        private router: Router,
        private zone: NgZone,
        private user: UserService,
        private culture: CultureService,
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    /// listFor a block's title as the page shows it: reported under its English, so a list is one list in every language.
    listNamed(title: string): AnalyticsList {
        return AnalyticsService.listFor(this.culture.english(title));
    }

    private get measurementId(): string {
        const config = (environment as any).analytics;
        return config && config.measurementId;
    }

    /// True while nothing may be sent: the owner is signed in, or was when tracking would have started.
    private get off(): boolean {
        if (this.ownerOff) { return true; }
        try {
            return this.user.isNijera();
        }
        catch {
            return false;
        }
    }

    /**
     * Loads gtag.js and starts page tracking. Call once, from the browser bootstrap.
     *
     * The landing page is recorded exactly once whichever side of its NavigationEnd this runs
     * on. From AppComponent it runs BEFORE: with initialNavigation 'enabled' the router pauses
     * the first navigation after its resolvers and finishes it from a bootstrap listener, which
     * Angular calls after the root component's first change detection - so the subscription
     * below catches it. When an early event starts tracking after the navigation instead, the
     * router.navigated branch sends it, and pageView() drops a repeat of the same URL.
     */
    init(): void {
        if (!this.isBrowser || this.started || !this.measurementId) { return; }
        this.started = true;

        try {
            const w = window as any;
            if (this.off) {
                // Google's own switch as well, in case anything else on the page configures this property.
                this.ownerOff = true;
                w['ga-disable-' + this.measurementId] = true;
                return;
            }

            if (typeof w.gtag !== 'function') {
                // index.html defines this before Tag Manager loads. Kept as a fallback so a
                // page served without it still measures instead of throwing.
                w.dataLayer = w.dataLayer || [];
                w.gtag = function () { w.dataLayer.push(arguments); };
            }

            const script = this.document.createElement('script');
            script.async = true;
            script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(this.measurementId);
            this.document.head.appendChild(script);

            const config: any = { send_page_view: false };
            if ((environment as any).analytics.debugMode) {
                // Shows events in GA4 DebugView as they happen.
                config.debug_mode = true;
            }

            this.command('js', new Date());
            this.command('config', this.measurementId, config);

            this.router.events
                .pipe(filter(event => event instanceof NavigationEnd))
                .subscribe(() => this.pageView());

            if (this.router.navigated) {
                this.pageView();
            }

            // A page view still waiting for its title when the tab closes or the site
            // navigates away (a login reloads the page) is sent now rather than lost.
            w.addEventListener('pagehide', () => this.flushPageView());
        }
        catch {
            // Measurement must never be the reason the site fails to start.
        }
    }

    /// Records that the visitor accepted the cookie notice. Stored consent is re-applied on
    /// the next visit by index.html, before any tag loads.
    grantConsent(): void {
        this.command('consent', 'update', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            analytics_storage: 'granted',
        });
    }

    /// Ties later events to a signed-in account, or clears it on sign-out. The account id is a
    /// GUID, never an email - Google's terms forbid sending anything that identifies a person.
    ///
    /// Queued rather than starting tracking, so calling it before init() puts the id on the
    /// landing page view too.
    setUser(userId: string): void {
        const id = userId || null;
        if (id === this.userId) { return; }

        this.userId = id;
        this.command('set', { user_id: id });
    }

    // ------------------------------------------------------------------ pages

    private pageView(): void {
        if (!this.isBrowser) { return; }

        const location = this.document.location.href;
        if (location === this.lastLocation || (this.pending && this.pending.location === location)) {
            return;
        }

        // A navigation that interrupts a waiting page view still counts that page.
        this.flushPageView();

        const first = this.lastLocation === null;
        this.pending = { location, referrer: this.lastLocation };
        this.lastLocation = location;
        this.listsSeen.clear();

        if (first) {
            // The server-rendered page already carries its own title.
            this.flushPageView();
            return;
        }

        const titleBefore = this.document.title;
        const started = Date.now();

        this.zone.runOutsideAngular(() => {
            this.pendingTimer = setInterval(() => {
                if (this.document.title !== titleBefore || Date.now() - started >= AnalyticsService.TitleWaitMs) {
                    this.flushPageView();
                }
            }, 100);
        });
    }

    private flushPageView(): void {
        if (this.pendingTimer) {
            clearInterval(this.pendingTimer);
            this.pendingTimer = null;
        }

        const page = this.pending;
        this.pending = null;
        if (!page) { return; }

        const params: any = {
            page_location: page.location,
            page_title: this.document.title,
        };

        // After the landing page, the referrer is the page before - gtag would otherwise keep
        // reporting the external referrer of the whole visit on every page.
        if (page.referrer) {
            params.page_referrer = page.referrer;
        }

        this.command('event', 'page_view', this.addressed(params));
    }

    // ------------------------------------------------------------------ product lists

    /// A list of products was shown - a category page, search results, a homepage carousel.
    viewItemList(products: any[], list: AnalyticsList): void {
        if (!this.isBrowser || !products || products.length === 0) { return; }

        const target = list || AnalyticsService.listFor(null);
        const signature = target.id + '|' + products.map(p => p && p.Slug).join(',');
        if (this.listsSeen.has(signature)) { return; }
        this.listsSeen.add(signature);

        this.ecommerce('view_item_list', {
            item_list_id: target.id,
            item_list_name: target.name,
            items: products.filter(p => !!p).map((p, index) => this.item(p, null, 1, index, target)),
        });
    }

    /// A product was clicked in a list.
    selectItem(product: any, list: AnalyticsList, index: number = null): void {
        if (!this.isBrowser || !product) { return; }

        const target = list || AnalyticsService.listFor(null);
        if (this.isEcho('select_item|' + product.Slug + '|' + target.id + '|' + index)) { return; }
        this.ecommerce('select_item', {
            item_list_id: target.id,
            item_list_name: target.name,
            items: [this.item(product, null, 1, index, target)],
        });
    }

    /// The list identity for a titled block. The id is derived from the name so the same block
    /// reports under the same id on every page it appears on.
    static listFor(name: string): AnalyticsList {
        const title = (name || '').trim() || 'Products';
        const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'products';
        return { id, name: title };
    }

    // ------------------------------------------------------------------ product and cart

    viewItem(product: any, option: any = null): void {
        if (!this.isBrowser || !product) { return; }

        const item = this.item(product, option, 1);
        this.ecommerce('view_item', { currency: AnalyticsService.Currency, value: item.price, items: [item] });
        this.fb('ViewContent', { content_ids: [item.item_id], content_type: 'product', value: item.price, currency: AnalyticsService.Currency });
    }

    addToCart(product: any, option: any, quantity: number): void {
        if (!this.isBrowser || !product || !(quantity > 0)) { return; }

        const item = this.item(product, option, quantity);
        const value = this.money(item.price * quantity);
        this.ecommerce('add_to_cart', { currency: AnalyticsService.Currency, value, items: [item] });
        this.fb('AddToCart', { content_ids: [item.item_id], content_type: 'product', value, currency: AnalyticsService.Currency });
    }

    removeFromCart(product: any, option: any, quantity: number): void {
        if (!this.isBrowser || !product || !(quantity > 0)) { return; }

        const item = this.item(product, option, quantity);
        this.ecommerce('remove_from_cart', { currency: AnalyticsService.Currency, value: this.money(item.price * quantity), items: [item] });
    }

    addToWishlist(product: any): void {
        if (!this.isBrowser || !product) { return; }

        const item = this.item(product, null, 1);
        this.ecommerce('add_to_wishlist', { currency: AnalyticsService.Currency, value: item.price, items: [item] });
        this.fb('AddToWishlist', { content_ids: [item.item_id], content_type: 'product' });
    }

    /// Not a GA4 recommended event, so it appears under its own name rather than in a funnel.
    addToCompare(product: any): void {
        if (!this.isBrowser || !product) { return; }

        const item = this.item(product, null, 1);
        this.ecommerce('add_to_compare', { currency: AnalyticsService.Currency, value: item.price, items: [item] });
    }

    /// A shopper asked to be emailed when a sold-out product is back (StockAlertService). Not a GA4 recommended event
    /// either: it shows under its own name - the demand for what is out of stock.
    stockAlert(product: any, option: any): void {
        if (!this.isBrowser || !product) { return; }

        const item = this.item(product, option, 1);
        this.ecommerce('stock_alert', { currency: AnalyticsService.Currency, value: item.price, items: [item] });
    }

    viewCart(items: ReadonlyArray<CartItem>): void {
        if (!this.isBrowser || !items || items.length === 0) { return; }
        this.ecommerce('view_cart', this.cartParams(items));
    }

    beginCheckout(items: ReadonlyArray<CartItem>): void {
        if (!this.isBrowser || !items || items.length === 0) { return; }

        const params = this.cartParams(items);
        this.ecommerce('begin_checkout', params);
        this.fb('InitiateCheckout', {
            content_ids: params.items.map((i: any) => i.item_id),
            content_type: 'product',
            num_items: params.items.reduce((sum: number, i: any) => sum + i.quantity, 0),
            value: params.value,
            currency: AnalyticsService.Currency,
        });
    }

    addShippingInfo(items: ReadonlyArray<CartItem>, shippingCharge: number): void {
        if (!this.isBrowser || !items || items.length === 0) { return; }

        const params = this.cartParams(items);
        params.shipping_tier = shippingCharge > 0 ? 'Standard shipping' : 'Free shipping';
        this.ecommerce('add_shipping_info', params);
    }

    /// paymentMethod is the PaymentMethod enum: 4 card (Stripe), 1 cash on delivery.
    addPaymentInfo(items: ReadonlyArray<CartItem>, paymentMethod: number): void {
        if (!this.isBrowser || !items || items.length === 0) { return; }

        const params = this.cartParams(items);
        params.payment_type = paymentMethod === 4 ? 'Card (Stripe)' : paymentMethod === 1 ? 'Cash on delivery' : 'Other';
        this.ecommerce('add_payment_info', params);
    }

    // ------------------------------------------------------------------ people

    search(term: string): void {
        const value = (term || '').trim();
        if (!this.isBrowser || !value || this.isEcho('search|' + value)) { return; }
        this.send('event', 'search', this.addressed({ search_term: value }));
    }

    /// method: 'email' or 'google'.
    login(method: string): void {
        this.send('event', 'login', this.addressed({ method }));
    }

    signUp(method: string): void {
        this.send('event', 'sign_up', this.addressed({ method }));
    }

    // ------------------------------------------------------------------ shapes

    /// True when the same action was already reported within the echo window. Records it either way.
    private isEcho(key: string): boolean {
        const now = Date.now();
        const last = this.lastAction.get(key);
        this.lastAction.set(key, now);
        return last !== undefined && now - last < AnalyticsService.EchoWindowMs;
    }

    private cartParams(items: ReadonlyArray<CartItem>): any {
        const lines = items
            .filter(line => line && line.product && line.quantity > 0)
            .map(line => this.item(line.product, line.option, line.quantity));

        const value = this.money(lines.reduce((sum, line) => sum + line.price * line.quantity, 0));
        return { currency: AnalyticsService.Currency, value, items: lines };
    }

    /**
     * One product as a GA4 item.
     *
     * item_id is the slug, not the ID: product listings come from OnlineProducts, whose ID is
     * not the Products ID the product page and the cart hold, so an ID-keyed item would be two
     * different products depending on where it was seen. The slug is the same everywhere -
     * including the order lines the server reports purchases from - and it is what the
     * Facebook pixel already used.
     */
    private item(product: any, option: any, quantity: number, index: number = null, list: AnalyticsList = null): any {
        const price = this.money(option && option.Price != null ? option.Price : product.Price);
        const regular = option && option.RegularPrice != null ? option.RegularPrice : product.RegularPrice;

        const item: any = {
            item_id: product.Slug || product.PUID || String(product.ID),
            item_name: product.Name,
            price,
            quantity,
        };

        if (product.BrandName) { item.item_brand = product.BrandName; }

        // CategoryTree is "a>b>c>" in slugs, root first - the order GA4's five levels expect.
        (product.CategoryTree || '')
            .split('>')
            .map((c: string) => c.trim())
            .filter((c: string) => !!c)
            .slice(0, 5)
            .forEach((category: string, level: number) => {
                item[level === 0 ? 'item_category' : 'item_category' + (level + 1)] = category;
            });

        // A listing card showing another colour than the main one names it (ShowcaseVariant).
        const variant = option
            ? AnalyticsService.variantName(option.Variant, option.VariantValue, option.Size)
            : (product.ShowcaseVariant || null);
        if (variant) { item.item_variant = variant; }

        if (regular > price) { item.discount = this.money(regular - price); }
        if (index !== null && index !== undefined) { item.index = index; }
        if (list) {
            item.item_list_id = list.id;
            item.item_list_name = list.name;
        }

        return item;
    }

    /// "Black / XL". Spelled exactly as the server spells it for purchases
    /// (PurchaseEvent.VariantName), so viewed, added and bought are one variant in the reports.
    static variantName(variant: string, variantValue: string, size: string): string {
        const name = variantValue && variantValue.trim() ? variantValue.trim() : (variant || '').trim();
        const parts = [name, (size || '').trim()].filter(part => !!part);
        return parts.length > 0 ? parts.join(' / ') : null;
    }

    private money(amount: number): number {
        return Math.round((Number(amount) || 0) * 100) / 100;
    }

    // ------------------------------------------------------------------ transport

    /// Queues a gtag command without starting anything. index.html defines gtag() before any
    /// tag loads, so a command queued early is still read, in order, once gtag.js arrives.
    private command(...args: any[]): void {
        if (!this.isBrowser || this.off) { return; }

        try {
            const w = window as any;
            if (typeof w.gtag === 'function') {
                w.gtag(...args);
            }
        }
        catch {
            // Reporting must never break the thing it is reporting on.
        }
    }

    /// A copy of an event's parameters addressed to this property alone. Without send_to, gtag
    /// delivers an event to every Google destination configured on the page - and the Tag
    /// Manager container configures a second property here - so until its tags are paused our
    /// events could land there as well. The copy keeps send_to out of the data layer mirror.
    private addressed(params: any): any {
        return Object.assign({}, params, { send_to: this.measurementId });
    }

    /// Sends an event, starting tracking first if nothing has yet. A product list can render
    /// before the root component gets round to init(), and an event queued ahead of gtag's
    /// config command has no property to go to.
    private send(...args: any[]): void {
        this.init();
        this.command(...args);
    }

    /**
     * Sends an ecommerce event to GA4, and mirrors it into the Tag Manager data layer in GA4's
     * standard shape for the tags that still live there (the Facebook pixel). The ecommerce
     * object is cleared first, as Google requires, so one event's items cannot leak into the next.
     */
    private ecommerce(name: string, params: any): void {
        if (this.off) { return; }
        this.send('event', name, this.addressed(params));

        try {
            const w = window as any;
            w.dataLayer = w.dataLayer || [];
            w.dataLayer.push({ ecommerce: null });
            w.dataLayer.push({ event: name, ecommerce: params });
        }
        catch { }
    }

    /// The Facebook pixel is loaded by the Tag Manager container, not by this app, so fbq only
    /// exists once that tag has fired.
    private fb(event: string, params: any): void {
        if (!this.isBrowser || this.off || typeof fbq === 'undefined') { return; }

        try {
            fbq('track', event, params);
        }
        catch { }
    }
}
